import { useCallback, useMemo } from 'react';

import useBonusProgramMetricAssignments from 'hooks/useBonusProgramMetricAssignments';

type BonusProgramUsageLookup = {
  [metricId: string]: FleetOps.BonusProgramMetricUsage[] | undefined;
};

/**
 * Bonus program metric assignments live server-side, not in Firestore content,
 * so unlike the other usage sources this hook fetches them once and exposes a
 * synchronous lookup. The lookup maps a metric id to the bonus programs (and
 * the config fields within them) that the metric feeds.
 */
const useGetBonusProgramUsage = () => {
  const { programAssignments } = useBonusProgramMetricAssignments();

  const usageMap = useMemo(() => {
    const newLookup: BonusProgramUsageLookup = {};
    programAssignments.forEach(({ program, metrics }) => {
      metrics.forEach((assignment) => {
        const forMetric = newLookup[assignment.metricId] ?? [];
        const existing = forMetric.find(
          (usage) => usage.programId === program.id,
        );
        if (existing) {
          existing.fieldNames.push(assignment.fieldName);
        } else {
          forMetric.push({
            programId: program.id,
            programName: program.name,
            fieldNames: [assignment.fieldName],
          });
        }
        newLookup[assignment.metricId] = forMetric;
      });
    });

    return newLookup;
  }, [programAssignments]);

  const getBonusProgramUsage = useCallback(
    (
      metric: Metrics.NormalMetric | Metrics.CompoundMetric,
    ): FleetOps.BonusProgramMetricUsage[] => usageMap[metric.id] ?? [],
    [usageMap],
  );

  return getBonusProgramUsage;
};

export default useGetBonusProgramUsage;
